"use client"
import { useState, useEffect } from "react";
import { Card, Pagination, Button, Spin, Empty, message } from "antd";
import { FileText, Download, Eye, Calendar, User } from "lucide-react";
import { getClinicalDecisionReports, downloadClinicalDecisionReport } from "./apis";
import { formatDate } from "./response-display/utils";

export default function ReportHistory({ onViewReport, isActive }) {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const perPage = 10;

    const fetchReports = async (page) => {
        setLoading(true);
        try {
            const response = await getClinicalDecisionReports(page, perPage);
            // Backend returns an array directly
            const reportsArray = Array.isArray(response) ? response : [];
            setReports(reportsArray);
            
            // Calculate pagination info
            // If we got exactly perPage items, there might be more pages
            const hasMore = reportsArray.length === perPage;
            setTotalItems(hasMore ? page * perPage : (page - 1) * perPage + reportsArray.length);
            setTotalPages(hasMore ? page + 1 : page);
        } catch (error) {
            console.error("Error fetching reports:", error);
            message.error("Failed to load reports. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isActive) {
            fetchReports(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, isActive]);

    const handleDownload = async (reportId) => {
        try {
            const blob = await downloadClinicalDecisionReport(reportId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `clinical-report-${reportId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Error downloading report:", error);
            message.error("Failed to download report. Please try again.");
        }
    };

    const handleView = (report) => {
        if (onViewReport) {
            onViewReport(report);
        }
    };

    if (loading && reports.length === 0) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spin size="large" />
            </div>
        );
    }

    if (!loading && reports.length === 0) {
        return (
            <div className="py-12">
                <Empty
                    description="No reports found"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </div>
        );
    }

    return (
        <div className="py-6">
            <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Report History</h2>
                    <span className="text-sm text-gray-500">
                        {totalItems} {totalItems === 1 ? 'report' : 'reports'}
                    </span>
                </div>

            <div className="space-y-3">
                {reports.map((report) => (
                    <Card
                        key={report.id}
                        className="hover:shadow-md transition-shadow"
                        bordered
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-5 h-5 text-[#0B7BC1]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-base font-semibold text-gray-900 mb-2 break-words">
                                            Report #{report.id}
                                        </h3>
                                        <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-2">
                                            {report.created_at && (
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span>{formatDate(report.created_at)}</span>
                                                </div>
                                            )}
                                            {report.age && (
                                                <div className="flex items-center gap-1">
                                                    <User className="w-4 h-4 text-gray-400" />
                                                    <span>{report.age} years, {report.gender || 'N/A'}</span>
                                                </div>
                                            )}
                                            {report.weight && (
                                                <div className="text-gray-600">
                                                    Weight: {report.weight} kg
                                                </div>
                                            )}
                                        </div>
                                        {report.chief_complaint && (
                                            <div className="mt-2">
                                                <span className="text-xs font-semibold text-gray-500 uppercase">Chief Complaint:</span>
                                                <p className="text-sm text-gray-700 mt-1 break-words">
                                                    {report.chief_complaint}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <Button
                                    icon={<Eye className="w-4 h-4" />}
                                    onClick={() => handleView(report)}
                                    className="flex items-center"
                                >
                                    View
                                </Button>
                                <Button
                                    icon={<Download className="w-4 h-4" />}
                                    onClick={() => handleDownload(report.id)}
                                    className="flex items-center"
                                >
                                    Download
                                </Button>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {totalPages > 1 && (
                <div className="flex justify-center pt-4">
                    <Pagination
                        current={currentPage}
                        total={totalItems}
                        pageSize={perPage}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                        showTotal={(total, range) =>
                            `Showing ${range[0]}-${range[1]} of ${total}+ reports`
                        }
                    />
                </div>
            )}
            </div>
        </div>
    );
}
